import { ThemeEvents, VariantUpdateEvent } from "@theme/events";

/**
 * A custom element that displays reward points based on price floor (1 point per full $1 spent).
 * This component listens for variant update events and updates the reward points text accordingly.
 * It calculates reward points by flooring the variant price (always rounds down).
 */
class RewardPointsText extends HTMLElement {
  /** @type {HTMLElement | null} */
  #textElement = null;

  connectedCallback() {
    const closestSection = this.closest(".shopify-section, dialog");
    if (!closestSection) return;

    closestSection.addEventListener(
      ThemeEvents.variantSelected,
      this.updateRewardPoints
    );

    this.#textElement = this.querySelector(".text-block");

    closestSection.addEventListener(
      ThemeEvents.variantUpdate,
      this.updateRewardPoints
    );
  }

  disconnectedCallback() {
    const closestSection = this.closest(".shopify-section, dialog");
    if (!closestSection) return;
    closestSection.addEventListener(
      ThemeEvents.variantSelected,
      this.updateRewardPoints
    );
    closestSection.removeEventListener(
      ThemeEvents.variantUpdate,
      this.updateRewardPoints
    );
  }

  /**
   * Updates the reward points text based on price floor (1 point per full $1).
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateRewardPoints = (event) => {
    if (event.type === 'variant:selected' || !event.detail?.resource) {
      if (event.target instanceof HTMLElement) {
        const variantPicker = event.target.closest('variant-picker') || event.target;
        const checkedInput = variantPicker.querySelector('input[type="radio"]:checked, input:checked');
        
        if (checkedInput && checkedInput.hasAttribute('data-variant-price')) {
          const rawPrice = Number(checkedInput.getAttribute('data-variant-price'));
          
          if (Number.isFinite(rawPrice)) {
            const priceInDollars = rawPrice / 100;
            const rewardPoints = Math.floor(priceInDollars);
            
            const textElement = this.#textElement || this.querySelector(".text-block, [class*='text-block'], div");
            if (textElement) {
              textElement.textContent = `Earn ${rewardPoints} Healthy Rewards Points`;
            }
            
            return; 
          }
        }
      }
    }
    if (event.detail?.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const productId = this.dataset.productId;
    if (!productId) return;

    // Prefer the selected variant payload to avoid stale/ambiguous DOM parsing.
    const variantPrice = Number(
      /** @type {{ price?: number } | null | undefined} */ (event.detail.resource)
        ?.price
    );
    let priceValue = Number.isFinite(variantPrice)
      ? variantPrice / 100
      : NaN;

    // Fallback for scenarios where variant payload is missing.
    if (Number.isNaN(priceValue)) {
      const priceContainer = event.detail.data.html?.querySelector(
        `product-price[data-product-id="${productId}"] [ref="priceContainer"]`
      );

      if (!priceContainer) return;

      const priceElement = priceContainer.querySelector(".price");
      if (!priceElement) return;

      const priceText =
        priceElement.textContent ||
        /** @type {HTMLElement} */ (priceElement).innerText;
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      if (!priceMatch) return;

      priceValue = parseFloat(priceMatch[0].replace(/,/g, ""));
      if (Number.isNaN(priceValue)) return;
    }

    // Floor to full dollars only - 1 point per full $1 spent (always round down)
    const rewardPoints = Math.floor(priceValue);

    // Update the text content
    const textElement =
      this.#textElement ||
      this.querySelector(".text-block, [class*='text-block'], div");

    if (textElement) {
      const rewardText = `Earn ${rewardPoints} Healthy Rewards Points`;
      textElement.textContent = rewardText;
    }
  };
}

if (!customElements.get("reward-points-text")) {
  customElements.define("reward-points-text", RewardPointsText);
}
